import { useTokenStore } from "../storage/tokenState";
import { queryClient } from '../App';
import { NAVIGATION_ROUTES } from "../constant";
import { PARTNER_ID_KEY } from "../appConstants/AppConstant";
import { clearPersistedStore } from "../store";
import { resetWithdrawStore } from "../storage/withdrawState";
import { KYC_PENDING_KEY } from "../pages/Kyc/helper";

/**
 * Keys a logout has no business touching.
 *
 * `localStorage.clear()` used to take these with it, and each loss had a cost:
 *
 * - `deviceID` is not a credential. `getDeviceId()` mints a fresh UUID when it
 *   is missing, so every logout made the same handset look like a new device to
 *   the backend, breaking device-level rate limiting, fraud signals and
 *   attribution.
 * - `language` is a UI preference. `resetTokenStore` already preserves it in
 *   store, so clearing the raw key contradicted that and reset Hindi users to
 *   English.
 * - `PARTNER_ID` is attribution context read before any auth exists.
 * - `kyc-digilocker-pending` holds a vendor `clientId`, not a credential, and is
 *   the *only* record that a verification was completed -- SurePass registers no
 *   webhook, so a lost clientId means the user authenticated at DigiLocker for
 *   nothing, and the next attempt pays for a fresh `initialize`, which is billed.
 *
 * `kyc-digilocker-popup-nonce` is deliberately absent: it identifies one popup
 * window within one page lifetime, and a logout ends that lifetime. Keeping it
 * could only let a stale message resolve a newer attempt.
 */
const PRESERVED_LOCAL_STORAGE_KEYS = [
  'deviceID',
  'language',
  PARTNER_ID_KEY,
  KYC_PENDING_KEY,
];

/** Collapses the burst of concurrent 401s a dead session produces into one reset. */
let resetInFlight = false;

/** Drops every localStorage key except the ones a logout has no claim on. */
function sweepLocalStorage() {
  try {
    const staleKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !PRESERVED_LOCAL_STORAGE_KEYS.includes(key)) {
        staleKeys.push(key);
      }
    }
    // Collected first: removing inside the loop shifts the indices.
    staleKeys.forEach(key => localStorage.removeItem(key));
  } catch {
    // Private mode or quota. The in-memory stores are already reset and the
    // reload starts from those.
  }
}

export const resetAllStores = () => {
  if (resetInFlight) return;

  // Already logged out: there is no session to end. Without this guard a
  // logged-out logout() still wiped storage and hard-navigated to the splash,
  // and whatever fired the 401 fired again on the next boot -- the loop that
  // stranded users returning from DigiLocker.
  const { token, resetTokenStore } = useTokenStore.getState();
  if (!token?.accessToken) return;

  resetInFlight = true;

  // Stores first, so anything they rewrite on reset is caught by the sweep below.
  resetTokenStore();
  resetWithdrawStore(); // sessionStorage, unaffected by the sweep
  clearPersistedStore();
  queryClient?.clear();

  sweepLocalStorage();

  // Unconditional on purpose. `useUserStore` and `useConfigStore` have no reset
  // of their own, so this reload is what actually clears them -- skipping it
  // would leave the app half-logged-in with a populated `userDetails`.
  setTimeout(() => {
    // Swept a second time because `persistor.purge()` writes asynchronously and
    // no reducer handles its `RESET_STORE` dispatch, so redux-persist can put
    // `persist:root` back -- still populated -- after the first sweep. Anything
    // it rewrote would otherwise rehydrate into the next session.
    sweepLocalStorage();
    window.location.href = NAVIGATION_ROUTES.SPLASH;
  }, 200);
};
